'use client';

import { useEffect, useRef, useState } from 'react';

type UseHideOnScrollOptions = {
  /** Minimum scroll delta before toggling visibility. */
  delta?: number;
  /** Always show the header when scroll position is below this. */
  topOffset?: number;
  enabled?: boolean;
};

function resolveScrollTop(target: EventTarget | null): number | null {
  if (!target || target === window || target === document) {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }
  if (target === document.documentElement || target === document.body) {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }
  if (!(target instanceof HTMLElement)) return null;

  // Ignore nested side panels, dialogs, and small scrollers.
  if (
    target.closest(
      'aside, [data-sidebar], [role="dialog"], [data-radix-scroll-area-viewport], .eh-sidebar-scroll',
    )
  ) {
    return null;
  }

  // Primary content scrollers: <main> or large viewport-filling panes.
  const isMain = target.tagName === 'MAIN';
  const fillsViewport = target.clientHeight >= window.innerHeight * 0.55;
  if (!isMain && !fillsViewport) return null;

  return target.scrollTop;
}

/** Hide chrome on scroll down; reveal on scroll up (works for window + main panes). */
export function useHideOnScroll({
  delta = 6,
  topOffset = 8,
  enabled = true,
}: UseHideOnScrollOptions = {}): boolean {
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    if (!enabled) {
      setHidden(false);
      return;
    }

    lastY.current = window.scrollY || document.documentElement.scrollTop || 0;

    const onScroll = (event: Event) => {
      const y = resolveScrollTop(event.target);
      if (y == null) return;

      const diff = y - lastY.current;
      if (y < topOffset) {
        setHidden(false);
      } else if (diff > delta) {
        setHidden(true);
      } else if (diff < -delta) {
        setHidden(false);
      }
      lastY.current = y;
    };

    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => document.removeEventListener('scroll', onScroll, true);
  }, [delta, topOffset, enabled]);

  return enabled ? hidden : false;
}

export function hideOnScrollNavClass(hidden: boolean): string {
  return [
    'transition-[transform,background-color,border-color,box-shadow] duration-300 ease-out will-change-transform',
    hidden
      ? '-translate-y-full pointer-events-none !bg-transparent !border-transparent !shadow-none'
      : 'translate-y-0',
  ].join(' ');
}
